import React from 'react';
import { Link } from 'react-router-dom';
import { GitBranch, CheckCircle, ArrowRight, Award, Zap, Shield, TrendingUp, Code, Rocket, GitMerge } from 'lucide-react';

export default function DevOpsAutomation() {
  const benefits = [
    {
      icon: <Rocket className="w-6 h-6" />,
      title: "Faster Deployments",
      description: "Deploy code to production 10x faster with automated CI/CD pipelines"
    },
    {
      icon: <Shield className="w-6 h-6" />,
      title: "Automated Security",
      description: "Built-in security scanning, vulnerability detection, and compliance checks"
    },
    {
      icon: <TrendingUp className="w-6 h-6" />,
      title: "Zero Downtime",
      description: "Blue-green and canary deployments ensuring continuous availability"
    },
    {
      icon: <Code className="w-6 h-6" />,
      title: "Code Quality",
      description: "Automated testing, code review, and quality gates in every pipeline"
    }
  ];

  const technologies = [
    { name: "CI/CD Tools", items: ["GitHub Actions", "GitLab CI", "Jenkins", "CircleCI", "ArgoCD"] },
    { name: "Version Control", items: ["Git", "GitHub", "GitLab", "Bitbucket"] },
    { name: "Automation", items: ["Ansible", "Chef", "Puppet", "Terraform"] },
    { name: "Testing", items: ["Jest", "Selenium", "Cypress", "SonarQube", "OWASP ZAP"] }
  ];

  const pipelineStages = [
    {
      stage: "Build",
      description: "Automated compilation, dependency resolution, and artifact generation",
      tools: ["Maven", "Gradle", "npm", "Docker"]
    },
    {
      stage: "Test",
      description: "Unit tests, integration tests, and automated security scans",
      tools: ["Jest", "JUnit", "SonarQube", "Snyk"]
    },
    {
      stage: "Security",
      description: "Vulnerability scanning, SAST, DAST, and dependency checks",
      tools: ["Trivy", "OWASP ZAP", "Checkmarx", "Veracode"]
    },
    {
      stage: "Deploy",
      description: "Automated deployment to staging and production environments",
      tools: ["ArgoCD", "Spinnaker", "Flux", "Octopus"]
    },
    {
      stage: "Monitor",
      description: "Real-time monitoring, alerting, and performance tracking",
      tools: ["Prometheus", "Grafana", "Datadog", "PagerDuty"]
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="relative min-h-[60vh] flex items-center pt-20 overflow-hidden">
        <div className="absolute inset-0 z-0">
          <img
            src="https://images.unsplash.com/photo-1618401471353-b98afee0b2eb?w=1920&auto=format&fit=crop&q=80"
            alt="DevOps CI/CD"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-purple-900/95 via-indigo-900/90 to-purple-900/95"></div>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 relative z-10">
          <div className="max-w-4xl">
            <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm border border-white/20 rounded-full px-4 py-2 text-sm text-white mb-6">
              <GitBranch className="w-4 h-4 text-green-400" />
              <span>Continuous Integration & Delivery</span>
            </div>

            <h1 className="text-5xl md:text-6xl font-bold text-white mb-6">
              DevOps CI/CD Automation
            </h1>

            <p className="text-xl text-gray-200 leading-relaxed mb-8">
              Accelerate software delivery with fully automated pipelines. Deploy faster,
              safer, and more frequently with comprehensive CI/CD automation.
            </p>

            <div className="flex flex-wrap gap-4">
              <a href="https://calendly.com/aneeshthedevops/hello-there-intro-call" target="_blank" rel="noopener noreferrer"
                className="bg-gradient-to-r from-yellow-400 to-orange-500 hover:from-yellow-500 hover:to-orange-600 text-gray-900 px-8 py-4 rounded-full font-bold transition-all hover:scale-105 shadow-2xl inline-flex items-center gap-2">
                Schedule Consultation <ArrowRight className="w-5 h-5" />
              </a>
              <Link to="/#case-studies"
                className="bg-white/10 hover:bg-white/20 backdrop-blur-sm border-2 border-white/30 text-white px-8 py-4 rounded-full font-bold transition-all inline-flex items-center gap-2">
                View Case Studies
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Key Benefits */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold mb-4">Why CI/CD Automation Matters</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Ship features faster while maintaining code quality and system reliability
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {benefits.map((benefit, idx) => (
              <div key={idx} className="bg-white rounded-2xl p-6 shadow-lg hover:shadow-xl transition-all">
                <div className="w-14 h-14 bg-gradient-to-br from-purple-600 to-indigo-600 rounded-xl flex items-center justify-center text-white mb-4">
                  {benefit.icon}
                </div>
                <h3 className="text-xl font-bold mb-2">{benefit.title}</h3>
                <p className="text-gray-600">{benefit.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pipeline Stages */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold mb-4">Complete Pipeline Automation</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              From code commit to production deployment - fully automated
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-5 gap-6">
            {pipelineStages.map((stage, idx) => (
              <div key={idx} className="bg-gradient-to-br from-purple-50 to-indigo-50 rounded-2xl p-6 hover:shadow-xl transition-all">
                <div className="w-12 h-12 bg-gradient-to-br from-purple-600 to-indigo-600 rounded-full flex items-center justify-center text-white font-bold text-lg mb-4">
                  {idx + 1}
                </div>
                <h3 className="text-xl font-bold mb-3 text-purple-600">{stage.stage}</h3>
                <p className="text-gray-700 mb-4 text-sm">{stage.description}</p>
                <div className="space-y-1">
                  {stage.tools.map((tool, toolIdx) => (
                    <div key={toolIdx} className="flex items-center gap-2 text-xs text-gray-600">
                      <CheckCircle className="w-3 h-3 text-green-600" />
                      {tool}
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Detailed Service Description */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="relative">
              <img
                src="https://images.unsplash.com/photo-1556075798-4825dfaaf498?w=800&auto=format&fit=crop&q=80"
                alt="CI/CD Pipeline"
                className="rounded-2xl shadow-2xl"
              />
              <div className="absolute -bottom-6 -right-6 bg-white rounded-2xl p-6 shadow-2xl max-w-xs">
                <div className="flex items-center gap-3 mb-2">
                  <GitMerge className="w-8 h-8 text-purple-600" />
                  <div className="text-3xl font-bold text-purple-600">100+</div>
                </div>
                <div className="text-sm text-gray-600">Pipelines Implemented</div>
              </div>
            </div>
            <div>
              <h2 className="text-4xl font-bold mb-6">
                Ship Code Faster & Safer
              </h2>
              <p className="text-lg text-gray-700 mb-6">
                I build enterprise-grade CI/CD pipelines that automate every step from code commit to production deployment. With experience implementing 100+ pipelines across GitHub Actions, GitLab CI, and Jenkins, I've helped teams reduce deployment time from days to minutes.
              </p>
              <div className="space-y-4">
                <div className="flex items-start gap-3">
                  <CheckCircle className="w-6 h-6 text-green-600 flex-shrink-0 mt-1" />
                  <div>
                    <h4 className="font-bold mb-1">GitOps Workflows</h4>
                    <p className="text-gray-600">Infrastructure and application deployment managed through Git with full auditability</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <CheckCircle className="w-6 h-6 text-green-600 flex-shrink-0 mt-1" />
                  <div>
                    <h4 className="font-bold mb-1">Automated Testing</h4>
                    <p className="text-gray-600">Unit, integration, and e2e tests running automatically on every commit</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <CheckCircle className="w-6 h-6 text-green-600 flex-shrink-0 mt-1" />
                  <div>
                    <h4 className="font-bold mb-1">Security Integration</h4>
                    <p className="text-gray-600">SAST, DAST, and dependency scanning integrated into every pipeline</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <CheckCircle className="w-6 h-6 text-green-600 flex-shrink-0 mt-1" />
                  <div>
                    <h4 className="font-bold mb-1">Deployment Strategies</h4>
                    <p className="text-gray-600">Blue-green, canary, and rolling deployments for zero-downtime releases</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Technologies */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold mb-4">Technologies & Tools</h2>
            <p className="text-xl text-gray-600">
              Modern DevOps toolchain for complete automation
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {technologies.map((tech, idx) => (
              <div key={idx} className="bg-white rounded-2xl p-6 shadow-lg border border-gray-100">
                <h3 className="font-bold text-lg mb-4 text-purple-600">{tech.name}</h3>
                <ul className="space-y-2">
                  {tech.items.map((item, itemIdx) => (
                    <li key={itemIdx} className="flex items-center gap-2 text-gray-700">
                      <div className="w-2 h-2 bg-purple-600 rounded-full"></div>
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Case Study */}
      <section className="py-20 bg-gradient-to-r from-purple-600 to-indigo-600 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <Award className="w-16 h-16 mx-auto mb-4" />
            <h2 className="text-4xl font-bold mb-4">Success Story</h2>
          </div>

          <div className="bg-white/10 backdrop-blur-sm rounded-3xl p-8 md:p-12 border border-white/20">
            <h3 className="text-3xl font-bold mb-4">E-Commerce Platform Automation</h3>

            <div className="grid md:grid-cols-2 gap-8 mb-8">
              <div>
                <h4 className="font-bold text-xl mb-3 flex items-center gap-2">
                  <div className="w-2 h-2 bg-red-400 rounded-full"></div>
                  Challenge
                </h4>
                <p className="text-purple-100">Manual deployments taking 2 days, frequent errors, and 20% deployment failure rate causing business disruption</p>
              </div>
              <div>
                <h4 className="font-bold text-xl mb-3 flex items-center gap-2">
                  <div className="w-2 h-2 bg-green-400 rounded-full"></div>
                  Solution
                </h4>
                <p className="text-purple-100">Built comprehensive GitHub Actions pipeline with automated testing, security scanning, and blue-green deployments</p>
              </div>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
              {[
                { metric: "Deployment Time", value: "15 minutes", improvement: "-99%" },
                { metric: "Success Rate", value: "99.5%", improvement: "+19.5%" },
                { metric: "Releases/Week", value: "50+", improvement: "+1,150%" },
                { metric: "Mean Time to Recovery", value: "5 minutes", improvement: "-88%" }
              ].map((result, idx) => (
                <div key={idx} className="text-center">
                  <div className="text-sm text-purple-200 mb-2">{result.metric}</div>
                  <div className="text-3xl font-bold mb-1">{result.value}</div>
                  <div className="text-xs text-green-300">{result.improvement}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-gradient-to-br from-gray-900 to-gray-800 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl font-bold mb-6">Ready to Automate Your Deployments?</h2>
          <p className="text-xl text-gray-300 mb-8">
            Let's build a CI/CD pipeline that accelerates your software delivery
          </p>
          <div className="flex flex-wrap gap-4 justify-center">
            <a href="https://calendly.com/aneeshthedevops/hello-there-intro-call" target="_blank" rel="noopener noreferrer"
              className="bg-gradient-to-r from-yellow-400 to-orange-500 hover:from-yellow-500 hover:to-orange-600 text-gray-900 px-10 py-5 rounded-full font-bold text-lg transition-all hover:scale-105 shadow-2xl inline-flex items-center gap-2">
              Schedule Free Consultation <ArrowRight className="w-6 h-6" />
            </a>
            <Link to="/"
              className="bg-white/10 backdrop-blur-sm border-2 border-white/30 hover:bg-white/20 text-white px-10 py-5 rounded-full font-bold text-lg transition-all inline-flex items-center gap-2">
              Back to Home
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
